import React, { useEffect } from "react";
import { Divider, Space, Tabs, Text, Title, Group } from "@mantine/core";
import axios from "axios";
import { shallow } from "zustand/shallow";

import GeneralSettings from "./GeneralSettings";
import RideChallengeSettings from "./RideChallengeSettings";
import { Counter } from "@components/shared";
import Intervals from "@components/Contest/ContestSettings/Intervals";

import useAppStore from "./effort-settings-store";
import { rideChallengeSettings } from "@components/RideChallenge/helpers";

export default function EffortSettings({
  effortId,
  fetchEffort,
  isRideChallenge,
  isVehicleDataEffort,
  beginDate = null,
  endDate = null,
  quickSettings = false,
  wizardSettings = {},
}) {
  const { settings, setSettings } = useAppStore(
    (state) => ({
      settings: state.settings,
      setSettings: state.setSettings,
    }),
    shallow
  );

  useEffect(() => {
    fetchSettings();
  }, []);

  function fetchSettings() {
    axios
      .get(`/efforts/${effortId}/settings/`)
      .then(({ data }) => {
        if (isRideChallenge) {
          setSettings({
            ...settings,
            ...rideChallengeSettings,
            ...data.response[0].settings,
          });
        } else {
          setSettings({ ...settings, ...data.response[0].settings });
        }
      })
      .catch((err) => {
        setSettings({});
      });
  }

  function updateSettings(newSettings) {
    const req = { campaign_effort_id: effortId, settings: newSettings };

    axios
      .post(`/efforts/${effortId}/settings/`, req)
      .then(() => {
        fetchSettings();
      })
      .then(() => fetchEffort())
      .catch((err) => {});
  }

  function onUpdate(newSettings) {
    setSettings({ ...settings, ...newSettings });
    updateSettings({ ...settings, ...newSettings });
  }

  const showKeywords = wizardSettings?.keyword_credits === true;

  if (quickSettings && showKeywords) {
    return (
      <KeywordSettings
        onUpdate={(e) => onUpdate({ ...e })}
        beginDate={beginDate}
        endDate={endDate}
        settings={settings}
        showTitle={false}
      />
    );
  }

  return (
    <div>
      <GeneralSettings
        isVehicleDataEffort={isVehicleDataEffort}
        onUpdate={(e) => onUpdate({ ...e })}
      />
      {(isRideChallenge || isVehicleDataEffort) && (
        <div>
          <Divider my="lg" />
          <RideChallengeSettings
            onUpdate={(e) => onUpdate({ ...e })}
            isVehicleDataEffort={isVehicleDataEffort}
            beginDate={beginDate}
            endDate={endDate}
          />
        </div>
      )}
      {showKeywords && (
        <>
          <Divider my="lg" />
          <KeywordSettings
            onUpdate={(e) => onUpdate({ ...e })}
            beginDate={beginDate}
            endDate={endDate}
            settings={settings}
          />
        </>
      )}
      <Text size="xs" c="dimmed" mt="lg">
        *changes saved automatically
      </Text>
    </div>
  );
}

const KeywordSettings = ({
  onUpdate,
  beginDate,
  endDate,
  settings,
  showTitle = true,
}) => {
  return (
    <div>
      {showTitle && (
        <Title order={4} mb="sm">
          Keyword Settings
        </Title>
      )}
      <Group mb="lg">
        <Counter
          unlimited={false}
          minValue={1}
          value={settings.keyword.keyword_credit_limit || 0}
          onChange={(e) => {
            onUpdate({
              keyword: {
                ...settings.keyword,
                keyword_credit_limit: e,
              },
            });
          }}
        />
        <Title order={3}>Total Credits Allowed</Title>
      </Group>
      <Group mb="lg">
        <Counter
          unlimited={false}
          minValue={1}
          value={settings.keyword.keyword_use_total || 0}
          onChange={(e) => {
            onUpdate({
              keyword: {
                ...settings.keyword,
                keyword_use_total: e,
              },
            });
          }}
        />
        <Title order={3}>Keyword Use Total</Title>
      </Group>
      <Intervals
        beginDate={beginDate}
        endDate={endDate}
        items={settings.keyword.intervals || []}
        title="Keyword Limit Interval"
        sectionTitle="Keyword Limit Intervals"
        onChange={(e) =>
          onUpdate({
            keyword: {
              ...settings.keyword,
              intervals: e,
            },
          })
        }
      />
    </div>
  );
};
